import posthog from "posthog-js";
import { useSignupFlowStore } from "./hooks/useSignUpFlowStore";

// PostHog flag for the "my details" email-capture experiment.
// Active variant "test" shows the email-only design and make first/last name optional.
// "Control" keeps the existing design + required names.
export const MY_DETAILS_TEST_FLAG = "my_details_test";

type FeatureFlagVariant = string | boolean;
type FeatureFlags = Record<string, FeatureFlagVariant>;

/**
 * The my-details experiment only applies to single-cat users, so MC users get a control experience.
 * If single-cat user switches to MC, ensure a stored assignment can't leak the test experience to them.
 */
export const isFlagDisabledForMultiCat = (flag: string, activeCatCount: number): boolean =>
  flag === MY_DETAILS_TEST_FLAG && activeCatCount > 1;

export const resolveFeatureFlagVariant = (
  flag: string,
  storedFeatureFlags: FeatureFlags | undefined,
  getFallbackVariant: () => FeatureFlagVariant | undefined,
  activeCatCount: number,
): FeatureFlagVariant | undefined => {
  if (isFlagDisabledForMultiCat(flag, activeCatCount)) {
    return false;
  }

  return storedFeatureFlags?.[flag] ?? getFallbackVariant();
};

export const mergeFeatureFlagsWithStoredOverrides = (
  resolvedFeatureFlags: FeatureFlags,
  storedFeatureFlags: FeatureFlags,
  activeCatCount: number,
): FeatureFlags => ({
  ...resolvedFeatureFlags,
  ...storedFeatureFlags,
  ...(isFlagDisabledForMultiCat(MY_DETAILS_TEST_FLAG, activeCatCount)
    ? { [MY_DETAILS_TEST_FLAG]: false }
    : {}),
});

/**
 * Non-React predicate that reads the resolved flag variant from the posthog-js
 * singleton. Used to make `requiredFieldsBySlug` flag-aware from non-React
 * callers (abandoned-cart routing, prefilled-field validation). Mirrors the
 * `inABTestGroup` helper in `useFlowNavigationContext` but without a hook.
 *
 * WARNING: this doesn't react to flag changes and can return a stale/not-yet-resolved value.
 * Only use when the flag was already resolved on an earlier page,
 * not immediately after bucketing / in the same view.
 */
export const inABTestGroupFromSingleton = (flag: string): boolean =>
  resolveFeatureFlagVariant(
    flag,
    useSignupFlowStore.getState().featureFlags,
    () => posthog?.getFeatureFlag?.(flag),
    useSignupFlowStore.getState().getActiveCatIds().length,
  ) === "test";
